<template>
  <div class="bbm-about">

    <!-- Hero -->
    <section class="bbm-about-hero">
      <div class="bbm-about-hero__bg"></div>
      <div class="bbm-container bbm-about-hero__content">
        <span class="bbm-label-caps">About the initiative</span>
        <h1 class="bbm-about-hero__title">Building the connected<br>capital of Europe</h1>
        <p class="bbm-about-hero__sub">
          A shared initiative connecting the organisations, communities, startups, investors,
          and institutions shaping Brussels.
        </p>
      </div>
    </section>

    <!-- Problem / Context -->
    <section class="bbm-section bbm-section--white">
      <div class="bbm-container">
        <div class="bbm-two-col">
          <div>
            <span class="bbm-label-caps">The challenge</span>
            <h2 class="bbm-section__title" style="margin-top: 8px;">Brussels already has exceptional initiatives.</h2>
            <div class="bbm-accent-bar"></div>
            <p class="bbm-body-lg" style="margin-top: 32px;">
              The challenge is visibility and connectivity.
            </p>
            <p class="bbm-body-lg" style="margin-top: 20px;">
              Across Brussels, thousands of startups, scaleups, hubs, investors, corporates, universities,
              public actors, and support organisations are actively shaping the future of Europe.
            </p>
            <p class="bbm-body-lg" style="margin-top: 20px;">
              Yet much of this ecosystem remains fragmented, difficult to navigate, and internationally
              underrepresented.
            </p>
          </div>
          <div class="bbm-initiative-box">
            <span class="material-symbols-outlined bbm-initiative-box__icon">hub</span>
            <h3 class="bbm-initiative-box__title">The Brussels Ecosystem initiative</h3>
            <p class="bbm-initiative-box__body">
              Aims to create a shared visibility and discovery layer that strengthens connections across
              the ecosystem and helps position Brussels as a leading European innovation capital.
            </p>
            <p class="bbm-initiative-box__tagline">
              Designed to amplify the organisations already shaping Brussels.
            </p>
          </div>
        </div>
      </div>
    </section>

    <!-- Why participate -->
    <section class="bbm-section">
      <div class="bbm-container">
        <div style="margin-bottom: 48px;">
          <span class="bbm-label-caps">Why organisations participate</span>
          <h2 class="bbm-section__title" style="margin-top: 8px;">What's in it for your organisation</h2>
        </div>
        <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div v-for="benefit in benefits" :key="benefit.title" class="bbm-benefit-card">
            <div class="bbm-benefit-card__header">
              <span class="material-symbols-outlined bbm-benefit-card__icon">{{ benefit.icon }}</span>
              <h3 class="bbm-benefit-card__title">{{ benefit.title }}</h3>
            </div>
            <p class="bbm-benefit-card__body">{{ benefit.description }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- Logo strip -->
    <section class="bbm-logo-strip">
      <div class="bbm-logo-strip__track">
        <img src="/images/static-home-page/brussels/beci-brussels-logo.png" alt="BECI" class="bbm-logo-strip__logo"/>
        <img src="/images/datascouts-logo.svg" alt="DataScouts" class="bbm-logo-strip__logo-ds"/>
        <img src="/images/static-home-page/brussels/beci-brussels-logo.png" alt="BECI" class="bbm-logo-strip__logo" aria-hidden="true"/>
        <img src="/images/datascouts-logo.svg" alt="DataScouts" class="bbm-logo-strip__logo-ds" aria-hidden="true"/>
        <img src="/images/static-home-page/brussels/beci-brussels-logo.png" alt="BECI" class="bbm-logo-strip__logo" aria-hidden="true"/>
        <img src="/images/datascouts-logo.svg" alt="DataScouts" class="bbm-logo-strip__logo-ds" aria-hidden="true"/>
        <img src="/images/static-home-page/brussels/beci-brussels-logo.png" alt="BECI" class="bbm-logo-strip__logo" aria-hidden="true"/>
        <img src="/images/datascouts-logo.svg" alt="DataScouts" class="bbm-logo-strip__logo-ds" aria-hidden="true"/>
        <img src="/images/static-home-page/brussels/beci-brussels-logo.png" alt="BECI" class="bbm-logo-strip__logo" aria-hidden="true"/>
        <img src="/images/datascouts-logo.svg" alt="DataScouts" class="bbm-logo-strip__logo-ds" aria-hidden="true"/>
        <img src="/images/static-home-page/brussels/beci-brussels-logo.png" alt="BECI" class="bbm-logo-strip__logo" aria-hidden="true"/>
        <img src="/images/datascouts-logo.svg" alt="DataScouts" class="bbm-logo-strip__logo-ds" aria-hidden="true"/>
        <img src="/images/static-home-page/brussels/beci-brussels-logo.png" alt="BECI" class="bbm-logo-strip__logo" aria-hidden="true"/>
        <img src="/images/datascouts-logo.svg" alt="DataScouts" class="bbm-logo-strip__logo-ds" aria-hidden="true"/>
        <img src="/images/static-home-page/brussels/beci-brussels-logo.png" alt="BECI" class="bbm-logo-strip__logo" aria-hidden="true"/>
        <img src="/images/datascouts-logo.svg" alt="DataScouts" class="bbm-logo-strip__logo-ds" aria-hidden="true"/>
        <img src="/images/static-home-page/brussels/beci-brussels-logo.png" alt="BECI" class="bbm-logo-strip__logo" aria-hidden="true"/>
        <img src="/images/datascouts-logo.svg" alt="DataScouts" class="bbm-logo-strip__logo-ds" aria-hidden="true"/>
        <img src="/images/static-home-page/brussels/beci-brussels-logo.png" alt="BECI" class="bbm-logo-strip__logo" aria-hidden="true"/>
        <img src="/images/datascouts-logo.svg" alt="DataScouts" class="bbm-logo-strip__logo-ds" aria-hidden="true"/>
        <img src="/images/static-home-page/brussels/beci-brussels-logo.png" alt="BECI" class="bbm-logo-strip__logo" aria-hidden="true"/>
        <img src="/images/datascouts-logo.svg" alt="DataScouts" class="bbm-logo-strip__logo-ds" aria-hidden="true"/>
      </div>
    </section>

    <!-- Testimonials -->
    <section class="bbm-section bbm-section--white">
      <div class="bbm-container">
        <div style="margin-bottom: 48px; text-align: center;">
          <span class="bbm-label-caps" style="justify-content: center; display: block;">Testimonials</span>
          <h2 class="bbm-section__title" style="margin-top: 8px;">What they say</h2>
        </div>
        <div class="bbm-quotes">
          <div
            v-for="(quote, i) in quotes"
            :key="i"
            :class="['bbm-quote-card', activeQuote === i ? 'active' : '']"
          >
            <span class="material-symbols-outlined bbm-quote-card__icon">format_quote</span>
            <p class="bbm-quote-card__text">{{ quote.text }}</p>
            <div class="bbm-quote-card__author">
              <span class="bbm-quote-card__name">{{ quote.name }}</span>
              <span class="bbm-quote-card__role">{{ quote.role }}</span>
            </div>
          </div>
          <div class="bbm-quotes__dots">
            <button
              v-for="(quote, i) in quotes"
              :key="i"
              :class="['bbm-quotes__dot', activeQuote === i ? 'active' : '']"
              @click="activeQuote = i"
            />
          </div>
        </div>
      </div>
    </section>

    <!-- What partners can do -->
    <section class="bbm-section bbm-section--white">
      <div class="bbm-container">
        <div style="margin-bottom: 48px;">
          <span class="bbm-label-caps">Partnership</span>
          <h2 class="bbm-section__title" style="margin-top: 8px;">What partners can do</h2>
        </div>
        <div class="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div v-for="offer in partnerOfferings" :key="offer.title" class="bbm-offer-card">
            <span class="material-symbols-outlined bbm-offer-card__icon">{{ offer.icon }}</span>
            <h3 class="bbm-offer-card__title">{{ offer.title }}</h3>
            <p class="bbm-offer-card__body">{{ offer.description }}</p>
            <ul v-if="offer.items" class="bbm-offer-card__list">
              <li v-for="item in offer.items" :key="item">{{ item }}</li>
            </ul>
          </div>
        </div>
      </div>
    </section>

    <!-- Become a partner form -->
    <section class="bbm-section bbm-section--tinted">
      <div class="bbm-container">
        <div class="bbm-partner-form-layout">
          <div class="bbm-partner-form__intro">
            <span class="bbm-label-caps">Get involved</span>
            <h2 class="bbm-section__title" style="margin-top: 8px;">How to become a partner</h2>
            <p class="bbm-partner-form__sub">Reach out and let's explore how you can get involved.</p>
            <div class="bbm-partner-form__contact">
              <p class="bbm-partner-form__contact-title">Let's get in touch</p>
              <a href="mailto:info@datascouts.eu" class="bbm-partner-form__contact-link">
                <span class="material-symbols-outlined">mail</span>
                info@datascouts.eu
              </a>
              <a href="tel:+32477280200" class="bbm-partner-form__contact-link">
                <span class="material-symbols-outlined">call</span>
                +32 477 280 200
              </a>
            </div>
          </div>

          <form class="bbm-partner-form" @submit.prevent="submitPartnerForm">
            <div v-if="formSubmitted" class="bbm-partner-form__success">
              <span class="material-symbols-outlined" style="font-size: 2.5rem; color: var(--primary-community);">check_circle</span>
              <p>Thank you! We'll be in touch shortly.</p>
            </div>
            <template v-else>
              <div class="bbm-partner-form__field">
                <label class="bbm-partner-form__label">Email *</label>
                <input v-model="partnerForm.email" type="email" class="bbm-partner-form__input" placeholder="your@email.com" required/>
              </div>
              <div class="tw-flex gap-4">
                <div class="bbm-partner-form__field" style="flex: 1;">
                  <label class="bbm-partner-form__label">First Name *</label>
                  <input v-model="partnerForm.firstName" type="text" class="bbm-partner-form__input" placeholder="First name" required/>
                </div>
                <div class="bbm-partner-form__field" style="flex: 1;">
                  <label class="bbm-partner-form__label">Last Name *</label>
                  <input v-model="partnerForm.lastName" type="text" class="bbm-partner-form__input" placeholder="Last name" required/>
                </div>
              </div>
              <div class="bbm-partner-form__field">
                <label class="bbm-partner-form__label">Company *</label>
                <input v-model="partnerForm.company" type="text" class="bbm-partner-form__input" placeholder="Your organisation" required/>
              </div>
              <button type="submit" class="bbm-partner-form__submit">Submit</button>
              <p class="bbm-partner-form__disclaimer">
                By submitting you agree to our <a href="https://datascouts.eu/privacypolicy/" target="_blank" class="bbm-partner-form__disclaimer-link">privacy policy</a>.
              </p>
            </template>
          </form>
        </div>
      </div>
    </section>

    <!-- Who it's for -->
    <section class="bbm-section">
      <div class="bbm-container">
        <div class="bbm-builders-block">
          <div class="bbm-builders-block__text">
            <span class="bbm-label-caps">A growing ecosystem of builders</span>
            <h2 class="bbm-section__title" style="margin-top: 8px;">The initiative is designed for</h2>
            <div class="bbm-accent-bar"></div>
          </div>
          <div class="bbm-builders-tags">
            <span v-for="type in ecosystemTypes" :key="type" class="bbm-tag">{{ type }}</span>
          </div>
        </div>
      </div>
    </section>

    <!-- CTA -->
    <section class="bbm-cta-final">
      <div class="bbm-cta-final__bg-icon material-symbols-outlined">language</div>
      <div class="bbm-container" style="position: relative; z-index: 1; text-align: center;">
        <span class="bbm-label-caps" style="color: rgba(255,255,255,0.5); justify-content: center; display: block;">One ecosystem. Thousands of connections.</span>
        <h2 class="bbm-cta-final__title">Let's strengthen the visibility and<br>connectivity of Brussels together.</h2>
        <div class="bbm-cta-final__actions">
          <button class="bbm-cta-final__btn bbm-cta-final__btn--primary" @click="$router.push('/register')">
            Become a Partner
          </button>
          <button class="bbm-cta-final__btn bbm-cta-final__btn--outline" @click="openFeedbackWindow">
            Schedule a Conversation
          </button>
        </div>
      </div>
    </section>

    <SimplifiedFooterBrussels/>

  </div>
</template>

<script lang="ts">
  import { defineComponent } from 'vue'
  import SimplifiedFooterBrussels from '@/components/SimplifiedFooter/SimplifiedFooterBrussels.vue'

  export default defineComponent({
    name: 'StaticHomeBrusselsAbout',
    components: {
      SimplifiedFooterBrussels,
    },
    data() {
      return {
        benefits: [
          {
            icon: 'travel_explore',
            title: 'Extend your visibility',
            description: 'Position your organisation within the broader Brussels ecosystem and become easier to discover internationally.',
          },
          {
            icon: 'hub',
            title: 'Connect beyond your own network',
            description: 'Create new opportunities across startups, corporates, investors, universities, and innovation communities.',
          },
          {
            icon: 'workspace_premium',
            title: 'Showcase your expertise',
            description: 'Highlight your initiatives, members, events, focus areas, and ecosystem role through a rich partner presence.',
          },
          {
            icon: 'public',
            title: 'Strengthen Brussels internationally',
            description: 'Contribute to a stronger collective positioning of Brussels as a connected European innovation ecosystem.',
          },
        ],
        partnerOfferings: [
          {
            icon: 'travel_explore',
            title: 'Ecosystem Visibility',
            description: 'Helping make the Brussels innovation ecosystem more visible and easier to discover.',
            items: [
              'Visibility across the ecosystem platform',
              'Showcasing organisations, initiatives, and events',
              'Shared storytelling around the Brussels ecosystem',
              'Participation in ecosystem-driven communication and visibility initiatives',
            ],
          },
          {
            icon: 'hub',
            title: 'Ecosystem Connectivity',
            description: 'Creating more opportunities for collaboration across the ecosystem.',
            items: [
              'Networking and ecosystem events',
              'Connections between organisations and communities',
              'Matchmaking and introductions',
              'Opportunities for cross-sector collaboration',
            ],
          },
          {
            icon: 'bolt',
            title: 'Ecosystem Momentum',
            description: 'Contributing to the growth and visibility of the Brussels innovation ecosystem.',
            items: [
              'Highlighting emerging initiatives and themes',
              'Supporting collaboration across the ecosystem',
              'Strengthening connections between ecosystem actors',
              'Helping position Brussels as a connected innovation ecosystem',
            ],
          },
        ],
        ecosystemTypes: [
          'Innovation hubs',
          'Startup communities',
          'Universities & research centres',
          'Investors & accelerators',
          'Public innovation actors',
          'Coworking spaces',
          'Clusters & sector organisations',
          'Ecosystem builders & networks',
        ],
        partnerForm: { email: '', firstName: '', lastName: '', company: '' },
        formSubmitted: false,
        activeQuote: 0,
        quoteTimer: null as any,
        quotes: [
          {
            name: 'Thierry',
            role: '',
            text: 'Brussels is not just the capital of Europe — it is the place where the right connections turn ambition into impact. This platform makes those connections visible.',
          },
          {
            name: 'Alain',
            role: '',
            text: 'What makes Brussels unique is its density of talent, institutions, and ideas in one compact city. Having a shared map of that ecosystem changes everything.',
          },
          {
            name: 'Ingrid',
            role: '',
            text: 'Innovation does not happen in isolation. Brussels has always been a city of networks — now we finally have the infrastructure to activate them.',
          },
        ],
      }
    },
    mounted() {
      this.quoteTimer = setInterval(() => {
        this.activeQuote = (this.activeQuote + 1) % this.quotes.length
      }, 5000)
    },
    unmounted() {
      clearInterval(this.quoteTimer)
    },
    methods: {
      async submitPartnerForm() {
        try {
          const response = await fetch('/api/brussels/partner-enquiry', {
            method: 'POST',
            credentials: 'same-origin',
            headers: {
              'Content-Type': 'application/json',
              'Accept': 'application/json',
              'x-csrf-token': (window as any).csrf,
            },
            body: JSON.stringify(this.partnerForm),
          })
          if (!response.ok) throw new Error('Request failed')
          this.formSubmitted = true
        } catch (e) {
          alert('Something went wrong. Please email us directly at info@datascouts.eu.')
        }
      },
      openFeedbackWindow() {
        document.querySelector('.userback-button-e').click()
      },
    },
  })
</script>

<style scoped lang="scss">
  @import url('https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@400;500;600;700&family=Inter:wght@400;500;600;700&display=swap');
  @import url('https://fonts.googleapis.com/css2?family=Material+Symbols+Outlined:wght,FILL@100..700,0..1&display=swap');

  $bbm-primary: var(--primary-community);
  $bbm-primary-container: var(--primary-community);
  $bbm-accent: #ff6f31;
  $bbm-secondary-container: #00ccf9;
  $bbm-on-surface-variant: #43474f;
  $bbm-surface: #f7f9fb;
  $bbm-outline-variant: #c3c6d1;

  .material-symbols-outlined {
    font-variation-settings: 'FILL' 0, 'wght' 400, 'GRAD' 0, 'opsz' 24;
  }

  .bbm-about {
    font-family: 'Inter', sans-serif;
    background: $bbm-surface;
    color: #191c1e;
    overflow-x: hidden;
  }

  // Hero
  .bbm-about-hero {
    position: relative;
    background: $bbm-primary-container;
    padding: 96px 0 80px;
    overflow: hidden;

    &::after {
      content: '';
      position: absolute;
      inset: 0;
      background: url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.04'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E");
      pointer-events: none;
      z-index: 0;
    }
  }

  .bbm-about-hero__bg {
    position: absolute;
    top: 0;
    right: 0;
    width: 40%;
    height: 100%;
    background: linear-gradient(to left, rgba(0, 204, 249, 0.12), transparent);
    pointer-events: none;
  }

  .bbm-about-hero__content {
    position: relative;
    z-index: 1;
  }

  .bbm-about-hero__title {
    font-family: 'Space Grotesk', sans-serif;
    font-size: clamp(2rem, 4vw, 3rem);
    font-weight: 700;
    letter-spacing: -0.02em;
    line-height: 1.1;
    color: white;
    margin: 12px 0 24px;
  }

  .bbm-about-hero__sub {
    font-size: 1.125rem;
    line-height: 1.6;
    color: rgba(255, 255, 255, 0.75);
    max-width: 640px;
    margin: 0;
  }

  // Shared
  .bbm-label-caps {
    display: block;
    font-family: 'Inter', sans-serif;
    font-size: 0.75rem;
    font-weight: 700;
    letter-spacing: 0.05em;
    text-transform: uppercase;
    color: $bbm-accent;
  }

  .bbm-container {
    max-width: 1280px;
    margin: 0 auto;
    padding: 0 40px;

    @media (max-width: 768px) {
      padding: 0 20px;
    }
  }

  .bbm-section {
    padding: 80px 0;
  }

  .bbm-section--white {
    background: white;
  }

  .bbm-section__title {
    font-family: 'Space Grotesk', sans-serif;
    font-size: clamp(1.75rem, 3vw, 2rem);
    font-weight: 600;
    color: $bbm-primary-container;
    margin: 0;
    line-height: 1.2;
  }

  .bbm-accent-bar {
    width: 64px;
    height: 4px;
    background: $bbm-accent;
    margin-top: 24px;
  }

  .bbm-body-lg {
    font-size: 1.0625rem;
    line-height: 1.65;
    color: $bbm-on-surface-variant;
    margin: 0;
  }

  // Two-col layout
  .bbm-two-col {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 64px;
    align-items: start;

    @media (max-width: 900px) {
      grid-template-columns: 1fr;
      gap: 48px;
    }
  }

  // Initiative highlight box
  .bbm-initiative-box {
    background: $bbm-primary-container;
    padding: 40px;
    color: white;
    position: relative;
    overflow: hidden;

    &::before {
      content: '';
      position: absolute;
      top: -30px;
      right: -30px;
      width: 200px;
      height: 200px;
      border-radius: 50%;
      background: rgba(0, 204, 249, 0.1);
      pointer-events: none;
    }
  }

  .bbm-initiative-box__icon {
    font-size: 2.5rem;
    color: $bbm-secondary-container;
    display: block;
    margin-bottom: 20px;
  }

  .bbm-initiative-box__title {
    font-family: 'Space Grotesk', sans-serif;
    font-size: 1.25rem;
    font-weight: 600;
    color: white;
    margin: 0 0 16px;
  }

  .bbm-initiative-box__body {
    font-size: 1rem;
    line-height: 1.65;
    color: rgba(255, 255, 255, 0.75);
    margin: 0 0 24px;
  }

  .bbm-initiative-box__tagline {
    font-size: 0.875rem;
    font-weight: 600;
    color: $bbm-secondary-container;
    margin: 0;
    font-style: italic;
  }

  // Benefit cards
  .bbm-benefit-card {
    background: white;
    border: 1px solid $bbm-outline-variant;
    padding: 32px;
    transition: border-color 0.2s, box-shadow 0.2s;

    &:hover {
      border-color: $bbm-accent;
      box-shadow: 0 8px 24px rgba(0, 0, 0, 0.07);
    }
  }

  .bbm-benefit-card__header {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 16px;
  }

  .bbm-benefit-card__icon {
    color: $bbm-accent;
    font-size: 1.75rem;
    flex-shrink: 0;
  }

  .bbm-benefit-card__title {
    font-family: 'Space Grotesk', sans-serif;
    font-size: 1.1rem;
    font-weight: 600;
    color: $bbm-primary-container;
    margin: 0;
  }

  .bbm-benefit-card__body {
    font-size: 0.9375rem;
    line-height: 1.6;
    color: $bbm-on-surface-variant;
    margin: 0;
  }

  // Offer cards
  .bbm-offer-card {
    background: $bbm-surface;
    border: 1px solid $bbm-outline-variant;
    border-left: 4px solid $bbm-accent;
    padding: 32px;
    transition: box-shadow 0.2s;

    &:hover {
      box-shadow: 0 6px 20px rgba(0, 0, 0, 0.07);
    }
  }

  .bbm-offer-card__icon {
    color: $bbm-accent;
    font-size: 2rem;
    display: block;
    margin-bottom: 16px;
  }

  .bbm-offer-card__title {
    font-family: 'Space Grotesk', sans-serif;
    font-size: 1.0625rem;
    font-weight: 600;
    color: $bbm-primary-container;
    margin: 0 0 12px;
  }

  .bbm-offer-card__body {
    font-size: 0.9rem;
    line-height: 1.6;
    color: $bbm-on-surface-variant;
    margin: 0;
  }

  .bbm-offer-card__list {
    margin: 12px 0 0 0;
    padding: 0 0 0 18px;
    list-style: disc;

    li {
      font-size: 0.875rem;
      line-height: 1.7;
      color: $bbm-on-surface-variant;
    }
  }

  // Ecosystem builders
  .bbm-builders-block {
    display: grid;
    grid-template-columns: 1fr 2fr;
    gap: 64px;
    align-items: center;

    @media (max-width: 900px) {
      grid-template-columns: 1fr;
      gap: 40px;
    }
  }

  .bbm-builders-tags {
    display: flex;
    flex-wrap: wrap;
    gap: 12px;
  }

  .bbm-tag {
    display: inline-block;
    padding: 10px 20px;
    background: white;
    border: 1px solid $bbm-outline-variant;
    font-family: 'Inter', sans-serif;
    font-size: 0.875rem;
    font-weight: 500;
    color: $bbm-primary-container;
    transition: background 0.15s, border-color 0.15s;

    &:hover {
      background: $bbm-primary-container;
      color: white;
      border-color: $bbm-primary-container;
    }
  }

  // Final CTA section
  .bbm-cta-final {
    background: $bbm-primary;
    padding: 100px 0;
    position: relative;
    overflow: hidden;
  }

  .bbm-cta-final__bg-icon {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    font-size: 600px;
    opacity: 0.04;
    color: white;
    pointer-events: none;
    line-height: 1;
  }

  .bbm-cta-final__title {
    font-family: 'Space Grotesk', sans-serif;
    font-size: clamp(1.75rem, 3vw, 2.5rem);
    font-weight: 600;
    color: white;
    margin: 16px 0 48px;
    line-height: 1.2;
  }

  .bbm-cta-final__actions {
    display: flex;
    gap: 16px;
    justify-content: center;
    flex-wrap: wrap;
  }

  .bbm-cta-final__btn {
    padding: 16px 32px;
    font-family: 'Space Grotesk', sans-serif;
    font-size: 1rem;
    font-weight: 600;
    border: none;
    cursor: pointer;
    transition: opacity 0.15s, transform 0.15s;

    &:hover {
      opacity: 0.9;
      transform: scale(1.02);
    }

    &--primary {
      background: $bbm-accent;
      color: white;
    }

    &--outline {
      background: transparent;
      color: white;
      border: 2px solid rgba(255, 255, 255, 0.5);

      &:hover {
        border-color: white;
        opacity: 1;
      }
    }
  }

  // Logo strip
  .bbm-logo-strip {
    overflow: hidden;
    background: #f8fafc;
    border-top: 1px solid #e2e8f0;
    border-bottom: 1px solid #e2e8f0;
    padding: 32px 0;
  }

  .bbm-logo-strip__track {
    display: flex;
    align-items: center;
    gap: 96px;
    width: max-content;
    animation: logo-scroll 30s linear infinite;
    padding: 0 48px;
  }

  .bbm-logo-strip__logo-ds {
    height: 120px;

    object-fit: contain;
    filter: grayscale(1) opacity(0.6);
    transition: filter 0.2s;

    &:hover {
      filter: grayscale(0) opacity(1);
    }
  }

  .bbm-logo-strip__logo {
    height: 60px;
    object-fit: contain;
    filter: grayscale(1) opacity(0.6);
    transition: filter 0.2s;

    &:hover {
      filter: grayscale(0) opacity(1);
    }
  }

  @keyframes logo-scroll {
    0% {
      transform: translateX(0);
    }
    100% {
      transform: translateX(-50%);
    }
  }

  // Quotes slider
  .bbm-quotes {
    position: relative;
    min-height: 220px;
  }

  .bbm-quote-card {
    display: none;
    flex-direction: column;
    align-items: center;
    text-align: center;
    max-width: 680px;
    margin: 0 auto;

    &.active {
      display: flex;
    }
  }

  .bbm-quote-card__icon {
    font-size: 3rem;
    color: var(--primary-community);
    opacity: 0.2;
    margin-bottom: 16px;
  }

  .bbm-quote-card__text {
    font-family: 'Space Grotesk', sans-serif;
    font-size: 1.2rem;
    line-height: 1.7;
    color: #1e3a5f;
    font-style: italic;
    margin: 0 0 28px;
  }

  .bbm-quote-card__author {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  .bbm-quote-card__name {
    font-weight: 700;
    font-size: 0.95rem;
    color: #1e3a5f;
  }

  .bbm-quote-card__role {
    font-size: 0.8rem;
    color: #64748b;
  }

  .bbm-quotes__dots {
    display: flex;
    justify-content: center;
    gap: 8px;
    margin-top: 32px;
  }

  .bbm-quotes__dot {
    width: 8px;
    height: 8px;
    border-radius: 9999px;
    background: #cbd5e1;
    border: none;
    cursor: pointer;
    padding: 0;
    transition: background 0.2s, transform 0.2s;

    &.active {
      background: var(--primary-community);
      transform: scale(1.3);
    }
  }

  // Partner form
  .bbm-partner-form-layout {
    display: flex;
    flex-direction: column;
    gap: 64px;

    @media (min-width: 768px) {
      flex-direction: row;
      align-items: flex-start;
      gap: 80px;
    }
  }

  .bbm-partner-form__intro {
    flex: 1;

    .bbm-partner-form__sub {
      font-size: 1rem;
      color: #475569;
      line-height: 1.7;
      margin: 16px 0 40px;
    }
  }

  .bbm-partner-form__contact {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  .bbm-partner-form__contact-title {
    font-size: 0.75rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: #94a3b8;
    margin: 0 0 4px;
  }

  .bbm-partner-form__contact-link {
    display: flex;
    align-items: center;
    gap: 8px;
    color: var(--primary-community);
    text-decoration: none;
    font-weight: 600;
    font-size: 0.95rem;

    .material-symbols-outlined {
      font-size: 18px;
      color: var(--primary-community);
    }

    &:hover {
      color: #ff6f31;

      .material-symbols-outlined {
        color: #ff6f31;
      }
    }
  }

  .bbm-partner-form {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 20px;
    background: white;
    padding: 40px;
    border-radius: 4px;
    box-shadow: 0 4px 24px rgba(0, 0, 0, 0.07);
  }

  .bbm-partner-form__field {
    display: flex;
    flex-direction: column;
    gap: 6px;
  }

  .bbm-partner-form__label {
    font-size: 0.75rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: #64748b;
  }

  .bbm-partner-form__input {
    padding: 12px 16px;
    border: 1.5px solid #e2e8f0;
    border-radius: 4px;
    font-size: 0.95rem;
    color: #1e3a5f;
    background: #f8fafc;
    transition: border-color 0.15s;
    outline: none;
    font-family: 'Inter', sans-serif;

    &:focus {
      border-color: var(--primary-community);
      background: white;
    }

    &::placeholder {
      color: #94a3b8;
    }
  }

  .bbm-partner-form__submit {
    padding: 14px;
    background: var(--primary-community);
    color: white;
    font-family: 'Space Grotesk', sans-serif;
    font-size: 0.85rem;
    font-weight: 700;
    letter-spacing: 0.05em;
    text-transform: uppercase;
    border: none;
    border-radius: 4px;
    cursor: pointer;
    transition: background 0.2s;
    margin-top: 4px;

    &:hover {
      background: #ff6f31;
    }
  }

  .bbm-partner-form__disclaimer {
    font-size: 0.75rem;
    color: #94a3b8;
    text-align: center;
    margin: 0;

    .bbm-partner-form__disclaimer-link {
      color: #64748b;
      text-decoration: underline;
    }
  }

  .bbm-partner-form__success {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 16px;
    padding: 40px 0;
    text-align: center;
    color: var(--primary-community);
    font-size: 1.1rem;
    font-weight: 600;
  }
</style>
